import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, TrendingUp, Gift, Users, ArrowUp, ArrowDown } from 'lucide-react';
import { useMerchantCoupons } from '../../hooks-v2/aggregator';
import { useCurrentMerchant } from '@/hooks-v2/context';
import { formatForDisplay, formatMoney, TimestampService } from '@zlinkx/shared-v2';
import { useTranslation } from '@/i18n';

const parseCouponTimestamp = (value: string | number | Date | null | undefined): Date | null => {
	if (value === null || value === undefined) return null;

	if (value instanceof Date) {
		return TimestampService.isValidDate(value) ? value : null;
	}

	if (typeof value === 'number') {
		const milliseconds = value < 10_000_000_000 ? value * 1000 : value;
		const parsed = TimestampService.getCurrentDate();
		parsed.setTime(milliseconds);
		return TimestampService.isValidDate(parsed) ? parsed : null;
	}

	const parsed = TimestampService.parseDateTimeInput(value);
	return TimestampService.isValidDate(parsed) ? parsed : null;
};

const isCouponExpired = (value: string | number | Date | null | undefined): boolean => {
	const parsed = parseCouponTimestamp(value);
	return parsed !== null && TimestampService.getMillisecondsSince(parsed) > 0;
};

const MerchantCoupons: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const translateWithFallback = (
		key: string,
		fallback: string,
		params?: Record<string, string | number>,
	): string => {
		const translated = t(key, params);
		return translated === key ? fallback : translated;
	};
	const colonSeparator = translateWithFallback('merchant.couponList.punctuation.colon', ':');

	// State declarations first
	const [searchTerm, setSearchTerm] = useState('');
	const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'expired'>('all');
	const [sortBy, setSortBy] = useState<'title' | 'created' | 'value' | 'type' | 'status'>('created');
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

	const { selectedMerchant, selectedMerchantId, loading: isLoadingMerchants } = useCurrentMerchant();
	const isDashboardLoading = isLoadingMerchants;

	const couponsData = useMerchantCoupons(selectedMerchantId ?? 0, {
		enabled: selectedMerchantId !== null,
		filters: {
			// Don't send status to API - frontend handles filtering
			search: searchTerm || undefined,
			sortBy: sortBy,
		},
	});

	// Extract data for compatibility with old component logic
	const coupons = couponsData.data.coupons;
	const isLoading = couponsData.isLoading;
	const error = couponsData.error;

	// Create volume info for compatibility
	const volumeInfo = {
		isWarning: coupons.length > 100,
		count: coupons.length,
		performance: {
			recommendation:
				coupons.length > 100
					? translateWithFallback(
							'merchant.couponList.performanceWarning.recommendation',
							'Consider using server-side filtering for better performance',
						)
					: '',
		},
	};

	// Pagination from the aggregator (backed by cursor-based useInfiniteQuery)
	const hasMore = couponsData.hasNextPage;
	const fetchNextPage = couponsData.fetchNextPage;
	const isFetchingNextPage = couponsData.isFetchingNextPage;

	// Data is already normalized by the new hooks and transformers
	// No need for manual normalization as the api-transformers handle this

	// Coupons are already normalized by the new hooks
	const normalizedCoupons = coupons;

	// Use computed statistics from the new hooks
	const stats = {
		totalCoupons: couponsData.computed.totalCount,
		activeCoupons: couponsData.computed.activeCount,
		expiredCoupons: couponsData.computed.expiredCount,
	};

	// Apply frontend filtering and sorting based on current state
	const filteredCoupons = useMemo(() => {
		let filtered = [...normalizedCoupons];

		// Apply status filter
		if (statusFilter !== 'all') {
			if (statusFilter === 'active') {
				filtered = filtered.filter(coupon => !isCouponExpired(coupon.expires_at));
			} else if (statusFilter === 'expired') {
				filtered = filtered.filter(coupon => isCouponExpired(coupon.expires_at));
			}
		}

		// Apply search filter
		if (searchTerm) {
			const searchLower = searchTerm.toLowerCase();
				filtered = filtered.filter(
					coupon =>
						coupon.title.toLowerCase().includes(searchLower) ||
						coupon.description.toLowerCase().includes(searchLower),
				);
			}

			// Apply sorting
			filtered.sort((a, b) => {
				switch (sortBy) {
					case 'title': {
						const comparison = a.title.localeCompare(b.title);
						return sortOrder === 'asc' ? comparison : -comparison;
					}
					case 'created': {
						const aCreatedAt = parseCouponTimestamp(a.created_at);
						const bCreatedAt = parseCouponTimestamp(b.created_at);
						const aAgeSeconds = aCreatedAt ? TimestampService.getSecondsSince(aCreatedAt) : Number.MAX_SAFE_INTEGER;
						const bAgeSeconds = bCreatedAt ? TimestampService.getSecondsSince(bCreatedAt) : Number.MAX_SAFE_INTEGER;
						const comparison = aAgeSeconds - bAgeSeconds;
						return sortOrder === 'asc' ? comparison : -comparison;
					}
					case 'value': {
						const comparison = a.coupon_value - b.coupon_value;
						return sortOrder === 'asc' ? comparison : -comparison;
					}
					case 'type': {
						const comparison = a.coupon_type.localeCompare(b.coupon_type);
						return sortOrder === 'asc' ? comparison : -comparison;
					}
					case 'status': {
						const comparison = Number(isCouponExpired(a.expires_at)) - Number(isCouponExpired(b.expires_at));
						return sortOrder === 'asc' ? comparison : -comparison;
					}
					default:
						return 0;
				}
			});

		return filtered;
	}, [normalizedCoupons, statusFilter, searchTerm, sortBy, sortOrder]);

	const handleSearch = (term: string) => {
		setSearchTerm(term);
		// Search is now handled by frontend filtering
	};

	const handleStatusFilter = (status: 'all' | 'active' | 'expired') => {
		setStatusFilter(status);
		// Status filtering is now handled by frontend filtering
	};

	const handleSort = (newSortBy: typeof sortBy) => {
		if (sortBy === newSortBy) {
			const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
			setSortOrder(newOrder);
		} else {
			setSortBy(newSortBy);
			setSortOrder('asc');
		}
		// Sorting is now handled by frontend filtering
	};

	const formatDiscount = (type: string, value: number) => {
		return type === 'percentage' ? `${value}%` : formatMoney(value);
	};

	const formatDate = (dateValue: string | number | Date | null | undefined) => {
		if (dateValue === null || dateValue === undefined) {
			return translateWithFallback('merchant.couponList.table.notAvailable', 'N/A');
		}

		const parsed = (() => {
			if (dateValue instanceof Date) {
				return TimestampService.isValidDate(dateValue) ? dateValue : null;
			}

			if (typeof dateValue === 'number') {
				const milliseconds = dateValue < 10_000_000_000 ? dateValue * 1000 : dateValue;
				const date = TimestampService.getCurrentDate();
				date.setTime(milliseconds);
				return TimestampService.isValidDate(date) ? date : null;
			}

			const date = TimestampService.parseDateTimeInput(dateValue);
			return TimestampService.isValidDate(date) ? date : null;
		})();

		if (!parsed) {
			console.warn('[Coupons] formatDate created invalid date from:', dateValue);
			return translateWithFallback('merchant.couponList.table.invalidDate', 'Invalid Date');
		}

		// Use centralized formatting with Date object
		return formatForDisplay(parsed);
	};

	// Loading state
	if (isDashboardLoading || isLoading) {
		return (
			<div className='flex-1 overflow-auto p-6'>
				<div className='animate-pulse'>
					<div className='h-8 bg-gray-200 rounded w-1/4 mb-6'></div>
						<div className='grid grid-cols-1 md:grid-cols-4 gap-6 mb-8'>
							{Array.from({ length: 4 }).map((_, i) => (
								<div key={i} className='h-24 bg-gray-200 rounded'></div>
							))}
						</div>
						<div className='space-y-4'>
							{Array.from({ length: 5 }).map((_, i) => (
								<div key={i} className='h-20 bg-gray-200 rounded'></div>
							))}
						</div>
				</div>
			</div>
		);
	}

	// No need for debug logs anymore

	// No merchant selected
	if (selectedMerchantId === null) {
		return (
			<div className='flex-1 overflow-auto p-6'>
				<div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
					<p className='text-yellow-600'>{t('merchant.couponList.noMerchantMessage')}</p>
				</div>
			</div>
		);
	}

	// Error state
	if (error !== null && error.length > 0) {
		return (
			<div className='flex-1 overflow-auto p-6'>
				<div className='bg-red-50 border border-red-200 rounded-lg p-4'>
						<div className='flex justify-between items-center'>
							<p className='text-red-600'>
								{t('merchant.couponList.errors.failedToLoad')}
								{colonSeparator} {error}
							</p>
						<button
							onClick={() => window.location.reload()}
							className='text-red-600 hover:text-red-800 font-medium'
						>
							{translateWithFallback('merchant.couponList.actions.dismiss', 'Dismiss')}
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='flex-1 overflow-auto p-4 sm:p-6'>
			{/* Header */}
			<div className='flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0'>
				<div>
					<h1 className='text-xl sm:text-2xl font-bold text-gray-900'>{t('merchant.couponList.title')}</h1>
					<p className='text-gray-500 text-sm sm:text-base'>
						{t('merchant.couponList.subtitle', { merchantName: selectedMerchant?.merchantName ?? '' })}
					</p>
				</div>
					<button
						onClick={() => {
							void navigate('/merchant/coupon-list/new');
						}}
					className='w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base'
				>
					<Plus className='w-4 h-4 mr-2' />
					{t('merchant.couponList.createCoupon')}
				</button>
			</div>

			{/* Stats Cards */}
			<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8'>
				<div className='bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6'>
					<div className='flex items-center'>
						<div className='bg-blue-100 p-2 sm:p-3 rounded-lg'>
							<Gift className='h-5 w-5 sm:h-6 sm:w-6 text-blue-600' />
						</div>
						<div className='ml-3 sm:ml-4'>
							<p className='text-xs sm:text-sm font-medium text-gray-500'>
								{t('merchant.couponList.stats.totalCoupons')}
							</p>
							<p className='text-xl sm:text-2xl font-bold text-gray-900'>{stats.totalCoupons}</p>
						</div>
					</div>
				</div>

				<div className='bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6'>
					<div className='flex items-center'>
						<div className='bg-green-100 p-2 sm:p-3 rounded-lg'>
							<TrendingUp className='h-5 w-5 sm:h-6 sm:w-6 text-green-600' />
						</div>
						<div className='ml-3 sm:ml-4'>
							<p className='text-xs sm:text-sm font-medium text-gray-500'>
								{t('merchant.couponList.stats.activeCoupons')}
							</p>
							<p className='text-xl sm:text-2xl font-bold text-gray-900'>{stats.activeCoupons}</p>
						</div>
					</div>
				</div>

				<div className='bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 sm:col-span-2 lg:col-span-1'>
					<div className='flex items-center'>
						<div className='bg-gray-100 p-2 sm:p-3 rounded-lg'>
							<Users className='h-5 w-5 sm:h-6 sm:w-6 text-gray-600' />
						</div>
						<div className='ml-3 sm:ml-4'>
							<p className='text-xs sm:text-sm font-medium text-gray-500'>
								{t('merchant.couponList.stats.expiredCoupons')}
							</p>
							<p className='text-xl sm:text-2xl font-bold text-gray-900'>{stats.expiredCoupons}</p>
						</div>
					</div>
				</div>
			</div>

			{/* Data Volume Warning */}
			{volumeInfo.isWarning && (
				<div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6'>
					<div className='flex'>
						<div className='flex-shrink-0'>
							<svg className='h-5 w-5 text-yellow-400' viewBox='0 0 20 20' fill='currentColor'>
								<path
									fillRule='evenodd'
									d='M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z'
									clipRule='evenodd'
								/>
							</svg>
						</div>
						<div className='ml-3'>
							<h3 className='text-sm font-medium text-yellow-800'>
								{translateWithFallback(
									'merchant.couponList.performanceWarning.title',
									`Performance Notice: ${volumeInfo.count} coupons loaded`,
									{ count: volumeInfo.count },
								)}
							</h3>
							<p className='text-sm text-yellow-700 mt-1'>{volumeInfo.performance.recommendation}</p>
						</div>
					</div>
				</div>
			)}

			{/* Search and Filters */}
			<div className='bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6'>
				<div className='flex flex-col space-y-4 lg:flex-row lg:items-center lg:space-y-0 lg:space-x-4'>
					{/* Search */}
					<div className='relative flex-1'>
						<Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
						<input
							type='text'
							placeholder={t('common.search')}
							value={searchTerm}
							onChange={e => handleSearch(e.target.value)}
							className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base'
						/>
					</div>

					{/* Status Filter */}
					<div className='flex items-center space-x-2'>
						<Filter className='w-4 h-4 text-gray-400 flex-shrink-0' />
						<select
							value={statusFilter}
							onChange={e => handleStatusFilter(e.target.value as 'all' | 'active' | 'expired')}
							className='w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base'
						>
							<option value='all'>{t('merchant.couponList.filters.allCoupons')}</option>
							<option value='active'>{t('merchant.couponList.filters.active')}</option>
							<option value='expired'>{t('merchant.couponList.filters.expired')}</option>
						</select>
					</div>
				</div>
			</div>

			{/* Coupons List */}
			<div className='bg-white rounded-xl shadow-sm border border-gray-200'>
				{filteredCoupons.length === 0 ? (
					<div className='p-6 sm:p-8 text-center'>
						<div className='text-gray-400 mb-4'>
							<Gift className='w-8 h-8 sm:w-12 sm:h-12 mx-auto' />
						</div>
						<h3 className='text-base sm:text-lg font-medium text-gray-900 mb-2'>
							{t('merchant.couponList.noCouponsFound')}
						</h3>
						<p className='text-gray-500 mb-4 text-sm sm:text-base'>
							{searchTerm || statusFilter !== 'all'
								? t('merchant.couponList.noCouponsFound')
								: t('merchant.couponList.createFirstCoupon')}
						</p>
						{!searchTerm && statusFilter === 'all' && (
								<button
									onClick={() => {
										void navigate('/merchant/coupon-list/new');
									}}
								className='inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base'
							>
								<Plus className='w-4 h-4 mr-2' />
								{t('merchant.couponList.createFirstCoupon')}
							</button>
						)}
					</div>
				) : (
					<div className='overflow-x-auto'>
						<table className='w-full'>
							<thead className='bg-gray-50'>
								<tr>
									<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
										<button
											onClick={() => handleSort('title')}
											className='flex items-center space-x-1 hover:text-gray-700'
										>
											<span>{t('merchant.couponList.table.title')}</span>
											{sortBy === 'title' && (
												<span className='ml-1'>
													{sortOrder === 'asc' ? (
														<ArrowUp className='w-3 h-3' />
													) : (
														<ArrowDown className='w-3 h-3' />
													)}
												</span>
											)}
										</button>
									</th>
									<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
										<button
											onClick={() => handleSort('value')}
											className='flex items-center space-x-1 hover:text-gray-700'
										>
											<span>{t('merchant.couponList.table.typeValue')}</span>
											{sortBy === 'value' && (
												<span className='ml-1'>
													{sortOrder === 'asc' ? (
														<ArrowUp className='w-3 h-3' />
													) : (
														<ArrowDown className='w-3 h-3' />
													)}
												</span>
											)}
										</button>
									</th>
									<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
										<button
											onClick={() => handleSort('status')}
											className='flex items-center space-x-1 hover:text-gray-700'
										>
											<span>{t('merchant.couponList.table.status')}</span>
											{sortBy === 'status' && (
												<span className='ml-1'>
													{sortOrder === 'asc' ? (
														<ArrowUp className='w-3 h-3' />
													) : (
														<ArrowDown className='w-3 h-3' />
													)}
												</span>
											)}
										</button>
									</th>
										<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
											<span>{t('merchant.couponList.table.expiration')}</span>
										</th>
										<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
											<span>{t('merchant.couponList.table.distributed')}</span>
										</th>
										<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
											<span>{t('merchant.couponList.table.redeemed')}</span>
										</th>
								</tr>
							</thead>
							<tbody className='bg-white divide-y divide-gray-200'>
									{filteredCoupons.map(coupon => (
											<tr
												key={coupon.coupon_id}
												className='hover:bg-gray-50 cursor-pointer'
												onClick={() => {
													void navigate(`/merchant/coupons/${coupon.coupon_id}`);
												}}
											>
											<td className='px-6 py-4 whitespace-nowrap'>
												<div className='text-sm text-gray-900'>{coupon.title}</div>
												<div className='text-xs text-gray-500'>{coupon.description}</div>
												{coupon.minimum_amount !== null && (
													<div className='text-xs text-gray-400'>
														{t('merchant.couponList.table.minimum')}
														{colonSeparator}{' '}
														{formatMoney(coupon.minimum_amount)}
													</div>
												)}
											</td>
											<td className='px-6 py-4 whitespace-nowrap'>
												<span
													className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
														coupon.coupon_type === 'percentage'
															? 'bg-blue-100 text-blue-800'
															: 'bg-green-100 text-green-800'
													}`}
												>
													{formatDiscount(coupon.coupon_type, coupon.coupon_value)}
												</span>
											</td>
											<td className='px-6 py-4 whitespace-nowrap'>
												{(() => {
													const expired = isCouponExpired(coupon.expires_at);
													return (
												<span
													className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
														expired ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
													}`}
												>
													{expired
														? t('merchant.couponList.table.expired')
														: t('merchant.couponList.table.active')}
												</span>
													);
												})()}
											</td>
											<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
												{formatDate(coupon.expires_at)}
											</td>
										<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
											{coupon.distributed_count ?? 0}
										</td>
										<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
											{coupon.redeemed_count ?? 0}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{/* Load More Button */}
			{hasMore && filteredCoupons.length > 0 && (
				<div className='mt-6 text-center'>
							<button
								onClick={() => {
									void fetchNextPage();
								}}
							disabled={isFetchingNextPage}
							className='inline-flex items-center px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
						>
							{isFetchingNextPage ? t('common.loading') : t('merchant.couponList.loadMore')}
						</button>
					</div>
				)}
		</div>
	);
};

export default MerchantCoupons;
